import { faSatelliteDish } from "@fortawesome/pro-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    DataProvidersListRequest,
} from "../../../../apiClient/generated";
import { useAppSelector, useDataProvidersApi } from "../../../../hooks";
import { CustomSwitch } from "../../../../ui/CustomSwitch";
import { useMapFilters } from "../../hooks/mapDataAndFilters";
import type { FilterEmissionsBy } from "../../state";
import { NumberInput, ToolBarDropdown } from "../../ui/MapControlsV2";
import {
    AreaSurveysCheckBoxMenu,
    DataProviderPickerCheckBoxMenu,
    MenuCategory,
} from "../../ui/Menu";

interface EmissionProviderPickerProps {
    label: string;
    selected:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
    setSelected: (
        value:
            | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
            | "all",
    ) => void;
    providerFilters: DataProvidersListRequest;
    disabled?: boolean;
}

export const EmissionProviderPicker = (props: EmissionProviderPickerProps) => {
    const apiClient = useDataProvidersApi();

    // Query filter groups
    const providersQuery = useQuery({
        queryKey: ["dataProviders", props.providerFilters],
        queryFn: async () => {
            const response = await apiClient.dataProvidersList({
                ...props.providerFilters,
            });
            return response.results;
        },
        staleTime: Infinity,
    });

    return (
        <DataProviderPickerCheckBoxMenu
            label={props.label}
            dataProviders={providersQuery.data}
            selected={props.selected}
            setSelected={props.setSelected}
            disabled={props.disabled}
        />
    );
};

const FilterByRateSection = () => {
    const [min, setMin] = useState<number>(null);
    const [max, setMax] = useState<number>(null);
    const [error, setError] = useState("");
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");

    // Sync local state with external filter state
    useEffect(() => {
        const externalMin = filterState?.emissions?.detectedRateMin;
        const externalMax = filterState?.emissions?.detectedRateMax;

        setMin(externalMin ?? null);
        setMax(externalMax ?? null);
    }, [
        filterState?.emissions?.detectedRateMin,
        filterState?.emissions?.detectedRateMax,
    ]);

    // Validate and apply filter when min or max changes
    useEffect(() => {
        // Clear previous errors
        setError("");

        // Validation logic
        if (min !== null && max !== null && min > max) {
            setError(
                "Minimum rate cannot be greater than or equal to maximum rate",
            );
            return;
        }

        if (min !== null && min < 0) {
            setError("Minimum rate cannot be negative");
            return;
        }

        if (max !== null && max < 0) {
            setError("Maximum rate cannot be negative");
            return;
        }
    }, [min, max, updateMapFilterState]);

    // Apply onBlur or if enter is pressed
    const applyFilters = () => {
        if (error === "") {
            updateMapFilterState((i) => {
                i.emissions.detectedRateMin = min;
                i.emissions.detectedRateMax = max;
            });
        }
    };

    const handleReset = () => {
        setMin(null);
        setMax(null);
        setError("");
        updateMapFilterState((i) => {
            i.emissions.detectedRateMax = null;
            i.emissions.detectedRateMin = null;
        });
    };

    return (
        <div
            onBlur={applyFilters}
            onKeyUp={(e) => e.key === "Enter" && applyFilters()}
        >
            <div className="flex items-center w-full justify-between gap-4 mb-1">
                <MenuCategory>Filter by Emission Rate</MenuCategory>
                <button
                    type="button"
                    className="text-xs text-ae-blue-550 hover:text-ae-blue-600 transition-colors"
                    onClick={handleReset}
                >
                    Reset rate
                </button>
            </div>
            <div className="flex w-full items-center gap-2">
                <NumberInput label="From" value={min} onChange={setMin} />
                <NumberInput label="To" value={max} onChange={setMax} />
            </div>
            <div className="min-h-3">
                {error !== "" && (
                    <span className="text-red-600 text-xs">{error}</span>
                )}
            </div>
        </div>
    );
};

const FilterDatePicker = () => {
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");

    return (
        <div>
            <div className="flex items-center w-full justify-between gap-4 mb-1">
                <MenuCategory>Filter by</MenuCategory>
            </div>
            <div className="flex w-full items-center gap-2">
                <select
                    value={filterState.emissions.filterEmissionsBy}
                    onChange={(e) => {
                        updateMapFilterState((s) => {
                            const value = e.target.value as FilterEmissionsBy;
                            s.emissions.filterEmissionsBy = value;
                            if (value === "match_date") {
                                s.emissions.emissionGroupFiltersV2.thirdPartyPublic =
                                    [];
                            }
                        });
                    }}
                    className="flex w-full border rounded items-center gap-2 text-xs p-1 border-ae-slate-200"
                >
                    <option value="detection_date">
                        Detection date (all detections)
                    </option>
                    <option value="match_date">
                        Match date (only matched emissions)
                    </option>
                </select>
            </div>
        </div>
    );
};

const EmissionsSection = () => {
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");
    const emissionFilters = useMemo(
        () => filterState.emissions,
        [filterState.emissions],
    );

    return (
        <div className="text-sm">
            <MenuCategory className="mt-2">
                Filter by emission type/provider
            </MenuCategory>
            <div className="my-1 mx-1 flex items-center gap-2">
                <CustomSwitch
                    size="xs"
                    checked={emissionFilters.showEmissions}
                    onChange={() => {
                        updateMapFilterState((s) => {
                            s.emissions.showEmissions =
                                !s.emissions.showEmissions;
                        });
                    }}
                />
                <span className="flex-grow">Show Company Emissions</span>
            </div>
            <EmissionProviderPicker
                label="3rd Party Detections"
                providerFilters={{
                    dataSource: ["THIRD_PARTY"],
                    hasData: true,
                }}
                selected={emissionFilters.emissionGroupFiltersV2.thirdParty}
                setSelected={(v) =>
                    updateMapFilterState((draft) => {
                        draft.emissions.emissionGroupFiltersV2.thirdParty = v;
                    })
                }
            />
            <EmissionProviderPicker
                label="Company Monitoring"
                providerFilters={{
                    dataSource: ["SELF_REPORTED"],
                    hasData: true,
                }}
                selected={emissionFilters.emissionGroupFiltersV2.selfReported}
                setSelected={(v) =>
                    updateMapFilterState((draft) => {
                        draft.emissions.emissionGroupFiltersV2.selfReported = v;
                    })
                }
            />
            <EmissionProviderPicker
                label="EPA SEP"
                providerFilters={{ dataSource: ["EPA"] }}
                selected={emissionFilters.emissionGroupFiltersV2.epa}
                setSelected={(v) =>
                    updateMapFilterState((draft) => {
                        draft.emissions.emissionGroupFiltersV2.epa = v;
                    })
                }
            />
        </div>
    );
};

const PublicEmissionsSection = () => {
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");
    const emissionFilters = useMemo(
        () => filterState.emissions,
        [filterState.emissions],
    );

    return (
        <div className="text-sm">
            <hr className="my-1" />
            <EmissionProviderPicker
                disabled={
                    filterState.emissions.filterEmissionsBy === "match_date"
                }
                label="Public Data"
                providerFilters={{ hasPublicData: true }}
                selected={
                    emissionFilters.emissionGroupFiltersV2.thirdPartyPublic
                }
                setSelected={(v) =>
                    updateMapFilterState((draft) => {
                        draft.emissions.emissionGroupFiltersV2.thirdPartyPublic =
                            v;
                    })
                }
            />
        </div>
    );
};

const UnmatchedDataPointsSection = () => {
    const canViewUnmatchedPoints = useAppSelector((s) =>
        s.auth.flags.includes("unmatched_emissions"),
    );
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");
    const emissionFilters = useMemo(
        () => filterState.emissions,
        [filterState.emissions],
    );

    if (!canViewUnmatchedPoints) {
        return null;
    }

    return (
        <div className="text-sm">
            <hr className="my-1" />
            <MenuCategory>Unmatched Data</MenuCategory>
            <EmissionProviderPicker
                label="Unmatched Detections"
                providerFilters={{}}
                selected={
                    emissionFilters.emissionGroupFiltersV2.unmatchedDataPoints
                }
                setSelected={(v) =>
                    updateMapFilterState((draft) => {
                        draft.emissions.emissionGroupFiltersV2.unmatchedDataPoints =
                            v;
                    })
                }
            />
        </div>
    );
};

const AreaSurveysSection = () => {
    const canViewExtraData = useAppSelector((s) =>
        s.auth.flags.includes("enable_area_surveys"),
    );

    const { filterState, updateMapFilterState } = useMapFilters("mainMap");

    if (!canViewExtraData) {
        return null;
    }

    return (
        <div className="text-sm">
            <hr className="my-1" />
            <AreaSurveysCheckBoxMenu
                label="Area Surveys"
                selected={filterState.extraData}
                setSelected={(value) =>
                    updateMapFilterState((d) => {
                        d.extraData = value;
                    })
                }
            />
        </div>
    );
};

interface EmissionsControlProps {
    className?: string;
}

export const EmissionsControl = (props: EmissionsControlProps) => {
    return (
        <ToolBarDropdown
            enabled={true}
            icon={faSatelliteDish}
            label="Data"
            className={props.className}
        >
            <div className="text-sm px-1">
                <FilterByRateSection />
                <FilterDatePicker />
                <EmissionsSection />
                <PublicEmissionsSection />
                <UnmatchedDataPointsSection />
                <AreaSurveysSection />
            </div>
        </ToolBarDropdown>
    );
};
